import { UserDto } from "../api";
import { getConfig } from "../config";

const basePath = getConfig().API_BASE_PATH;

// --- Types ---

export interface AuthTokenResponse {
  accessToken: string;
  expiresIn: number;
  user: UserDto;
}

// --- In-memory access token ---

let accessToken: string | null = null;
let refreshTimerId: number | null = null;
let onUnauthorized: (() => void) | null = null;
let bootstrapPromise: Promise<AuthTokenResponse | null> | null = null;

export function getAccessToken(): string | null {
  return accessToken;
}

// --- Bootstrap ---

/**
 * Restores the session from the httpOnly refresh cookie, once per page load.
 *
 * The access token lives in memory only, so it is null on every page load until
 * a /refresh round-trip completes. React runs child effects before parent ones,
 * so a page-level fetch would otherwise fire before App's bootstrap even starts
 * and go out unauthenticated. Public endpoints answer those with 200 instead of
 * 401, so nothing triggers a retry and the anonymous payload sticks.
 *
 * Every request awaits this in the `pre` middleware, and App awaits the same
 * promise, so the /refresh call still happens exactly once.
 */
export function bootstrapAuth(): Promise<AuthTokenResponse | null> {
  if (!bootstrapPromise) {
    bootstrapPromise = tryRefreshToken();
  }

  return bootstrapPromise;
}

export function setOnUnauthorized(callback: () => void) {
  onUnauthorized = callback;
}

// --- Login ---

export async function login(username: string, password: string): Promise<AuthTokenResponse> {
  const response = await fetch(`${basePath}/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ username, password })
  });

  if (!response.ok) {
    throw response;
  }

  const data: AuthTokenResponse = await response.json();
  accessToken = data.accessToken;
  scheduleTokenRefresh(data.expiresIn);

  return data;
}

// --- Token Refresh ---

function scheduleTokenRefresh(expiresInSeconds: number) {
  if (refreshTimerId) {
    clearTimeout(refreshTimerId);
  }

  // Refresh 60 seconds before expiry (minimum 5 seconds)
  const refreshInMs = Math.max((expiresInSeconds - 60) * 1000, 5000);

  refreshTimerId = window.setTimeout(() => {
    tryRefreshToken();
  }, refreshInMs);
}

export async function tryRefreshToken(): Promise<AuthTokenResponse | null> {
  try {
    const response = await fetch(`${basePath}/refresh`, {
      method: "POST",
      credentials: "include"
    });

    if (!response.ok) {
      accessToken = null;
      return null;
    }

    const data: AuthTokenResponse = await response.json();
    accessToken = data.accessToken;
    scheduleTokenRefresh(data.expiresIn);

    return data;
  } catch {
    accessToken = null;
    return null;
  }
}

// --- Logout ---

export async function logout(): Promise<boolean> {
  if (refreshTimerId) {
    clearTimeout(refreshTimerId);
    refreshTimerId = null;
  }

  try {
    const response = await fetch(`${basePath}/logout`, {
      method: "POST",
      credentials: "include"
    });

    if (response.ok) {
      accessToken = null;
      return true;
    }
  } catch {
    // Network error
  }

  return false;
}

export function handleUnauthorizedResponse() {
  accessToken = null;
  if (refreshTimerId) {
    clearTimeout(refreshTimerId);
    refreshTimerId = null;
  }
  onUnauthorized?.();
}
